import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  decide,
  type CalendarConnection,
  type CalendarProvider,
  type ProviderBaaRecord,
} from '@emr/shared';

import {
  disconnectCalendarConnection,
  fetchCalendarSyncConflicts,
  fetchOwnCalendarConnections,
  fetchProviderBaaRecords,
  resolveCalendarSyncConflict,
  startCalendarConnection,
  updateCalendarConnection,
  uploadProviderBaaRecord,
} from '../api/calendarSync.ts';
import { ApiError } from '../api/client.ts';
import { useGrants } from '../auth/sessionContext.ts';
import { AsyncSection } from '../components/AsyncState.tsx';
import { Button, ErrorText, Field, Panel } from '../components/primitives.tsx';
import { useDateTimeFormat } from '../format/dateTimeFormatContext.ts';
import { formatDateAndTime } from '../format/formatDateTime.ts';

/**
 * Google Calendar / Outlook Calendar two-way sync settings (`BACKLOG.md` K7,
 * `docs/decisions/calendar-sync-scope.md`'s "Where this gets configured" section). No page-level
 * capability gate (`GatedRoute` is not used here) — most of this page is self-service, reachable by
 * any authenticated provider for their own connections, mirroring `SignaturePage.tsx`'s own
 * self-service framing rather than `PracticeBrandingPage.tsx`'s admin-only one. The BAA section
 * below is the one part gated inline on `org:manage_settings`, the same "page open, one section
 * gated" shape `PracticeBrandingPage.tsx`'s own `LogoSection` uses for `whiteLabel`.
 */
export function ProviderCalendarSyncPage() {
  const { t } = useTranslation('calendarSync');
  const grants = useGrants();
  const canManageSettings = decide(grants, 'org:manage_settings');
  const canManageSchedule = decide(grants, 'schedule:manage');

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h1 className="m-0 text-xl font-semibold text-ink">{t('heading')}</h1>
      </div>
      <p className="m-0 text-sm text-ink-muted">{t('intro')}</p>
      <ConnectionsSection />
      {canManageSettings ? <BaaSection /> : null}
      {canManageSchedule ? <ConflictsSection /> : null}
    </div>
  );
}

function describeError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return fallback;
}

// --- Connections (self-service) ------------------------------------------------------------------

const VENDORS: readonly CalendarProvider[] = ['google', 'microsoft'];

function ConnectionsSection() {
  const { t } = useTranslation('calendarSync');
  const query = useQuery({
    queryKey: ['calendarSync', 'connections'],
    queryFn: ({ signal }) => fetchOwnCalendarConnections(signal),
  });

  return (
    <Panel title={t('connections.heading')}>
      <AsyncSection query={query} loadingLabel={t('connections.loading')}>
        {(connections) => (
          <div className="flex flex-col gap-4">
            {VENDORS.map((vendor) => {
              const connection = connections.find((c) => c.calendarProvider === vendor);
              return connection === undefined ? (
                <DisconnectedRow key={vendor} vendor={vendor} />
              ) : (
                <ConnectedRow key={vendor} vendor={vendor} connection={connection} />
              );
            })}
          </div>
        )}
      </AsyncSection>
    </Panel>
  );
}

function DisconnectedRow({ vendor }: { readonly vendor: CalendarProvider }) {
  const { t } = useTranslation('calendarSync');
  const start = useMutation({
    mutationFn: () => startCalendarConnection(vendor),
    onSuccess: (authorizationUrl) => {
      window.location.href = authorizationUrl;
    },
  });

  const notConfigured =
    start.isError &&
    start.error instanceof ApiError &&
    start.error.code === 'provider_not_configured';

  return (
    <div className="flex flex-col gap-2 border-b border-line pb-4 last:border-0 last:pb-0">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-ink">{t(`connections.${vendor}`)}</span>
        <Button
          type="button"
          variant="secondary"
          pending={start.isPending}
          onClick={() => {
            start.mutate();
          }}
        >
          {t('connections.connect')}
        </Button>
      </div>
      {notConfigured ? (
        <ErrorText
          message={t('connections.notConfigured', { vendor: t(`connections.${vendor}`) })}
        />
      ) : start.isError ? (
        <ErrorText message={describeError(start.error, t('connections.connectError'))} />
      ) : null}
    </div>
  );
}

function ConnectedRow({
  vendor,
  connection,
}: {
  readonly vendor: CalendarProvider;
  readonly connection: CalendarConnection;
}) {
  const { t } = useTranslation('calendarSync');
  const { preference } = useDateTimeFormat();
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['calendarSync', 'connections'] });

  const toggleSync = useMutation({
    mutationFn: (syncEnabled: boolean) => updateCalendarConnection(connection.id, { syncEnabled }),
    onSuccess: invalidate,
  });
  const togglePhi = useMutation({
    mutationFn: (phiInclusionEnabled: boolean) =>
      updateCalendarConnection(connection.id, { phiInclusionEnabled }),
    onSuccess: invalidate,
  });
  const disconnect = useMutation({
    mutationFn: () => disconnectCalendarConnection(connection.id),
    onSuccess: invalidate,
  });

  const phiBlockedByBaa =
    togglePhi.isError &&
    togglePhi.error instanceof ApiError &&
    togglePhi.error.code === 'baa_not_current';

  return (
    <div className="flex flex-col gap-2 border-b border-line pb-4 last:border-0 last:pb-0">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-ink">{t(`connections.${vendor}`)}</span>
          <span className="text-xs text-ink-muted">
            {connection.lastSyncedAt === null
              ? t('connections.neverSynced')
              : t('connections.lastSyncedAt', {
                  when: formatDateAndTime(connection.lastSyncedAt, preference),
                })}
          </span>
        </div>
        <Button
          type="button"
          variant="secondary"
          pending={disconnect.isPending}
          onClick={() => {
            disconnect.mutate();
          }}
        >
          {t('connections.disconnect')}
        </Button>
      </div>

      {connection.lastSyncError !== null ? (
        <ErrorText message={t('connections.lastSyncError', { error: connection.lastSyncError })} />
      ) : null}

      <label className="flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          checked={connection.syncEnabled}
          disabled={toggleSync.isPending}
          onChange={(event) => {
            toggleSync.mutate(event.target.checked);
          }}
          className="h-4 w-4 rounded border-field-line"
        />
        {t('connections.syncEnabledLabel')}
      </label>
      <p className="m-0 pl-6 text-xs text-ink-muted">{t('connections.syncEnabledHint')}</p>
      {toggleSync.isError ? (
        <ErrorText message={describeError(toggleSync.error, t('connections.toggleError'))} />
      ) : null}

      <label className="flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          checked={connection.phiInclusionEnabled}
          disabled={togglePhi.isPending}
          onChange={(event) => {
            togglePhi.mutate(event.target.checked);
          }}
          className="h-4 w-4 rounded border-field-line"
        />
        {t('connections.phiInclusionLabel')}
      </label>
      <p className="m-0 pl-6 text-xs text-ink-muted">{t('connections.phiInclusionHint')}</p>
      {phiBlockedByBaa ? (
        <p className="m-0 pl-6 text-xs text-warning-text">
          {t('connections.phiInclusionNeedsBaa')}
        </p>
      ) : togglePhi.isError ? (
        <ErrorText message={describeError(togglePhi.error, t('connections.toggleError'))} />
      ) : null}

      {disconnect.isError ? (
        <ErrorText message={describeError(disconnect.error, t('connections.disconnectError'))} />
      ) : null}
    </div>
  );
}

// --- BAA records (org:manage_settings) -------------------------------------------------------------

function BaaSection() {
  const { t } = useTranslation('calendarSync');
  const { preference } = useDateTimeFormat();
  const query = useQuery({
    queryKey: ['calendarSync', 'baa'],
    queryFn: ({ signal }) => fetchProviderBaaRecords(signal),
  });

  return (
    <Panel title={t('baa.heading')}>
      <p className="m-0 mb-4 text-sm text-ink-muted">{t('baa.intro')}</p>
      <AsyncSection query={query} loadingLabel={t('baa.loading')}>
        {(records) => (
          <div className="flex flex-col gap-4">
            {records.length === 0 ? (
              <p className="m-0 text-sm text-ink-muted">{t('baa.empty')}</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {records.map((record) => (
                  <BaaRecordRow key={record.id} record={record} preference={preference} />
                ))}
              </ul>
            )}
            <BaaUploadForm />
          </div>
        )}
      </AsyncSection>
    </Panel>
  );
}

function BaaRecordRow({
  record,
  preference,
}: {
  readonly record: ProviderBaaRecord;
  readonly preference: Parameters<typeof formatDateAndTime>[1];
}) {
  const { t } = useTranslation('calendarSync');
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-control border border-line px-3 py-2 text-sm">
      <span className="font-medium text-ink">{record.calendarProvider}</span>
      <span
        className={
          record.status === 'current'
            ? 'rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent-text'
            : 'rounded-full bg-field px-2 py-0.5 text-xs font-medium text-ink-muted'
        }
      >
        {record.status === 'current' ? t('baa.statusCurrent') : t('baa.statusSuperseded')}
      </span>
      <span className="text-ink-muted">{t('baa.until', { date: record.currentUntil })}</span>
      <span className="text-ink-muted">
        {t('baa.uploadedAt', { when: formatDateAndTime(record.uploadedAt, preference) })}
      </span>
    </li>
  );
}

function BaaUploadForm() {
  const { t } = useTranslation('calendarSync');
  const queryClient = useQueryClient();
  const [providerId, setProviderId] = useState('');
  const [provider, setProvider] = useState<CalendarProvider>('google');
  const [currentUntil, setCurrentUntil] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation({
    mutationFn: () => {
      if (file === null) return Promise.reject(new Error('no file selected'));
      return uploadProviderBaaRecord(providerId, provider, file, currentUntil);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendarSync', 'baa'] });
      setFile(null);
    },
  });

  const isFileTypeError =
    upload.isError &&
    upload.error instanceof ApiError &&
    upload.error.code === 'document_content_mismatch';

  return (
    <form
      className="flex flex-col gap-3 border-t border-line pt-4"
      onSubmit={(event) => {
        event.preventDefault();
        upload.mutate();
      }}
    >
      <h3 className="m-0 text-sm font-semibold text-ink">{t('baa.uploadHeading')}</h3>
      <Field
        label={t('baa.providerLabel')}
        value={providerId}
        onChange={(event) => {
          setProviderId(event.target.value);
        }}
        placeholder="00000000-0000-0000-0000-000000000000"
        required
      />
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-ink" htmlFor="calendar-sync-baa-vendor">
          {t('baa.vendorLabel')}
        </label>
        <select
          id="calendar-sync-baa-vendor"
          value={provider}
          onChange={(event) => {
            setProvider(event.target.value === 'microsoft' ? 'microsoft' : 'google');
          }}
          className="rounded-control border border-field-line bg-field px-3 py-2 text-sm text-ink"
        >
          <option value="google">{t('connections.google')}</option>
          <option value="microsoft">{t('connections.microsoft')}</option>
        </select>
      </div>
      <Field
        label={t('baa.currentUntilLabel')}
        type="date"
        value={currentUntil}
        onChange={(event) => {
          setCurrentUntil(event.target.value);
        }}
        required
      />
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-ink" htmlFor="calendar-sync-baa-file">
          {t('baa.fileLabel')}
        </label>
        <input
          id="calendar-sync-baa-file"
          type="file"
          accept="application/pdf"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
          }}
          className="text-sm text-ink"
        />
      </div>
      {isFileTypeError ? (
        <ErrorText message={t('baa.fileTypeError')} />
      ) : upload.isError ? (
        <ErrorText message={describeError(upload.error, t('baa.uploadError'))} />
      ) : upload.isSuccess ? (
        <p role="status" className="m-0 text-sm font-medium text-accent-text">
          {t('baa.uploadSuccess')}
        </p>
      ) : null}
      <Button type="submit" pending={upload.isPending} disabled={file === null}>
        {t('baa.upload')}
      </Button>
    </form>
  );
}

// --- Sync conflicts (schedule:manage) --------------------------------------------------------------

function ConflictsSection() {
  const { t } = useTranslation('calendarSync');
  const { preference } = useDateTimeFormat();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['calendarSync', 'conflicts'],
    queryFn: ({ signal }) => fetchCalendarSyncConflicts(undefined, signal),
  });

  const resolve = useMutation({
    mutationFn: (conflictId: string) => resolveCalendarSyncConflict(conflictId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendarSync', 'conflicts'] });
    },
  });

  return (
    <Panel title={t('conflicts.heading')}>
      <p className="m-0 mb-4 text-sm text-ink-muted">{t('conflicts.intro')}</p>
      <AsyncSection query={query} loadingLabel={t('conflicts.loading')}>
        {(conflicts) =>
          conflicts.length === 0 ? (
            <p className="m-0 text-sm text-ink-muted">{t('conflicts.empty')}</p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {conflicts.map((conflict) => (
                <li
                  key={conflict.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-control border border-line px-3 py-2 text-sm"
                >
                  <span className="text-ink-muted">
                    {t('conflicts.detectedAt', {
                      when: formatDateAndTime(conflict.detectedAt, preference),
                    })}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    pending={resolve.isPending && resolve.variables === conflict.id}
                    onClick={() => {
                      resolve.mutate(conflict.id);
                    }}
                  >
                    {t('conflicts.resolve')}
                  </Button>
                </li>
              ))}
            </ul>
          )
        }
      </AsyncSection>
      {resolve.isError ? (
        <div className="mt-2">
          <ErrorText message={describeError(resolve.error, t('conflicts.resolveError'))} />
        </div>
      ) : null}
    </Panel>
  );
}
